import { isEmpty, isString, isEqual } from "lodash-es";
import { DocumentIndex, SearchResult } from "ndx";

import * as Cmp from "./comparison";
import {
    DataView,
    DataExtractor,
    ExtractorExpander,
    getExtractors,
    getAllExtractors,
    forEach,
    DataIterator,
} from "./dataView";
import { customOrDefaultFilterTransformer } from "./dataView/extractor";
import { assertNever, clone, set } from "./obj";

const MATCH_TYPE_EXACT_IGNORE_CASE = "ExactIgnoreCase";
const MATCH_TYPE_WILDCARD = "Wildcard";

type MatchType =
    | typeof MATCH_TYPE_EXACT_IGNORE_CASE
    | typeof MATCH_TYPE_WILDCARD;

/**
 * A filter for filtering data in a {DataSource}.
 *
 * @remarks
 *
 * A {SimpleAndFilter} contains a list of {SimpleOrFilter}. A {SimpleOrFilter}
 * filters the values returned by a given extractor.
 */
interface SimpleAndFilter {
    filters: Array<SimpleOrFilter>;
}

/**
 * A filter for filtering the values returned by an extractor.
 *
 * @remarks
 *
 * A {SimpleOrFilter} contains a list of rules which determine what to include
 * and exclude in the list of values returned by an extractor.
 *
 * The generic filter value (FV) should not be used directly if unknown.
 * DataExtractor.customFilter().asString() is the correct way to interact with this value.
 */
interface SimpleOrFilter<FV = unknown> {
    extractorId: string; // Or the extractor itself?
    filters?: Array<SimpleFieldFilter<FV>>;
    exclusionFilters?: Array<SimpleFieldFilter<FV>>;
}

/**
 * The generic filter value (FV) should not be used directly if unknown.
 * DataExtractor.customFilter().asString() is the correct way to interact with this value.
 */
interface SimpleFieldFilter<FV = unknown> {
    value: FV;
    matchType: MatchType;
}

interface FieldIndex<ITEM, VAL, FV> {
    extractor: DataExtractor<ITEM, VAL, FV>;
    uniqueValueToItemIds: Map<
        string,
        { label: string; itemIds: Array<string>; value: FV }
    >;
}

/**
 * Data returned by filtering a data view using a {DataViewIndex}.
 *
 * @remarks
 *
 * The query result contains a new, filtered data view.
 */
interface QueryResult<ITEM> {
    // A new data view, with only the matched items, in the same order as the original
    dataView: DataView<ITEM>;
    orResults: Array<OrResult>;
    // The list of item ids matched by the exclusion filters of the query
    excludedItemIds: Set<string> | null;
}

/*
 * All the matches for a particular extractor
 */
interface OrResult {
    extractorId: string;
    matchGroups: Array<MatchGroup>;
}

interface MatchResult extends OrResult {
    extractorId: string;
    matchGroups: Array<MatchGroup>;
    wildcardMatchGroup: MatchGroup;
}

interface MatchGroup<FV = unknown> {
    value: FV;
    itemIds: Array<string>;
}

const DEFAULT_SPLIT_REGEXP = /([ \t\r\n.!\/?:,;+*(){}\[\]"'<>´‘\\^$|\-]+)/;

const defaultSplitter: Splitter = {
    split: (str, callback) => {
        let wasLastFragmentSeparator = true;
        // Split will return "" when the string starts with a separator
        str.split(DEFAULT_SPLIT_REGEXP).forEach((fragment) => {
            const isSeparator = !wasLastFragmentSeparator;
            if (fragment !== "") {
                callback(fragment, isSeparator);
            }
            wasLastFragmentSeparator = isSeparator;
        });
    },
};

interface TypeMatcher<ITEM> {
    /**
     * The matcher takes a non sanitized `value`. It is the implementation
     * responsibility to sanitize it via `sanitize()` before querying
     * `fieldIndex` and then calling `onMatch`.
     */
    match: <FV>(
        value: string,
        fieldIndex: FieldIndex<ITEM, any, FV>,
        onMatch: (uniqueValue: string) => void,
        sanitize: (value: string) => string
    ) => void;
}

interface FieldFilterTransformer<ITEM> {
    /**
     * The extractor ID to which the transformer is associated.
     */
    extractorId: string;
    /**
     * It can transform a field filter associated to `extractorId` to
     * a field filter that can be used technically (e.g. in a URL). The
     * use case is a field filter with localized value (e.g. workflow
     * status text) created by the typeahead component. It is transformed
     * to a field filter with a technical value (e.g. a technical
     * workflow status) and a custom `matchType`. Don't forget to return
     * the appropriate matchers in `customTypeMatchers`.
     */
    toTechnicalFilter: (filter: SimpleFieldFilter) => SimpleFieldFilter;
    /**
     * It can transform a field filter associated to `extractorId` to
     * a field filter that can be used for display. The use case is a
     * field filter with technical value (see `toTechnicalFilter`) and a
     * custom `matchType` coming from a URL. It is transformed to a field
     * filter with a localized value (e.g. workflow status text).
     */
    toDisplayFilter: (filter: SimpleFieldFilter) => SimpleFieldFilter;
    /**
     * It builds a set of additional matchers with which the index is
     * populated during construction. That allows filtering customization
     * via custom `matchType` values.
     */
    customTypeMatchers: () => Partial<Record<MatchType, TypeMatcher<ITEM>>>;
}

function splitQuery(splitter: Splitter, query: string): string[] {
    const result: Array<string> = [];
    splitter.split(query, (fragment, isSeparator) => {
        if (!isSeparator) {
            result.push(fragment);
        }
    });
    return result;
}

function fieldFilterValueAsString<FV>(
    fieldFilter: SimpleFieldFilter<FV>,
    asString: (value: FV) => string
): string {
    if (fieldFilter.matchType === MATCH_TYPE_WILDCARD) {
        if (isString(fieldFilter.value)) {
            return fieldFilter.value;
        } else {
            throw new Error(
                `In case of MATCH_TYPE_WILDCARD, value is expected to be a string, got: ${JSON.stringify(
                    fieldFilter
                )}`
            );
        }
    } else {
        return asString(fieldFilter.value);
    }
}

/**
 * The underlying machinery for the {Filter} component.
 *
 * @remarks
 *
 * A {DataViewIndex} contains a data view. It uses an efficient full-text
 * index. It can be used to filter its underlying data view and build a new,
 * filtered data view.
 *
 * It accepts a list of extractor ids. If present, only these extractors will
 * be used to index the data view. For nested extract values, expanders can be
 * provided. Each value returned by the expander will be indexed.
 *
 * In addition, a list of {FieldFilterTransformer} can serve to serialize and deserialize filters.
 */
class DataViewIndex<ITEM> {
    private readonly typeMatchers: Partial<
        Record<MatchType, TypeMatcher<ITEM>>
    >;
    private readonly dataView: DataView<ITEM>;
    private readonly fullTextIndex: DocumentIndex<string, string>;
    private readonly fieldIndexes: Array<FieldIndex<ITEM, any, unknown>>;
    private readonly fieldFilterTransformers?: Array<
        FieldFilterTransformer<ITEM>
    >;
    readonly splitter: Splitter;

    /**
     * @param dataView - the data view to index
     * @param indexedExtractorIds - list of extractor ids to use for indexing
     * (if {undefined}, all the extractors of the data view will be used)
     * @param fieldFilterTransformers - filter transformers for filters with a
     * technical value (eg, filters which are localized)
     * @param splitter - the splitter used to split the query
     */
    constructor(
        dataView: DataView<ITEM>,
        indexedExtractorIds?: Array<string>,
        extractorExpanders?: Array<ExtractorExpander<any, any>>,
        fieldFilterTransformers?: Array<FieldFilterTransformer<ITEM>>,
        splitter: Splitter = defaultSplitter
    ) {
        this.typeMatchers = {};
        this.typeMatchers[MATCH_TYPE_EXACT_IGNORE_CASE] = {
            match: (value, fieldIndex, onMatch, sanitize) => {
                const query = sanitize(value);
                if (fieldIndex.uniqueValueToItemIds.has(query)) {
                    onMatch(query);
                }
            },
        };
        this.typeMatchers[MATCH_TYPE_WILDCARD] = {
            match: (value, fieldIndex, onMatch, sanitize) => {
                const query = sanitize(value);
                for (const uniqueValue of fieldIndex.uniqueValueToItemIds.keys()) {
                    if (uniqueValue.indexOf(query) >= 0) {
                        onMatch(uniqueValue);
                    }
                }
            },
        };
        if (fieldFilterTransformers) {
            fieldFilterTransformers.forEach((trans) => {
                const customTypeMatchers = trans.customTypeMatchers();
                if (customTypeMatchers) {
                    Object.entries(customTypeMatchers).forEach(
                        ([matchType, matcher]) => {
                            // SAFETY: matchType is a key of `customTypeMatchers` which is of type `Partial<Record<MatchType, TypeMatcher<ITEM>>>`,
                            // so matchType is guaranteed to be of type MatchType.
                            const mt = matchType as MatchType;
                            this.typeMatchers[mt] = matcher;
                        }
                    );
                }
            });
        }

        const startDate = new Date();
        this.dataView = dataView;
        this.fieldFilterTransformers = fieldFilterTransformers;
        this.splitter = splitter;

        const extractorsForIndexing = indexedExtractorIds
            ? getExtractors(dataView, indexedExtractorIds)
            : getAllExtractors(dataView);
        if (extractorsForIndexing.length === 0) {
            throw new Error("No extractors");
        }
        extractorsForIndexing.forEach((extractor) => {
            if (extractor.id.indexOf(":") >= 0) {
                throw new Error(
                    "The character ':' in extractor ids is forbidden"
                );
            }
        });
        const fullTextIndex = new DocumentIndex<string, string>({
            tokenizer: (query: string) => splitQuery(this.splitter, query),
            filter: (term: string) => term,
        });
        fullTextIndex.addField("uniqueValue", {
            getter: (uniqueValue: string) => uniqueValue,
        });
        const fieldIndexes = extractorsForIndexing.map((extractor) => {
            const fieldIndex: FieldIndex<ITEM, any, unknown> = {
                extractor,
                uniqueValueToItemIds: new Map(),
            };
            return fieldIndex;
        });
        const iter = dataView.dataSource.iter();
        let i = 0;
        let extractorIdToExpander: {
            [extractorId: string]: ExtractorExpander<any, any>;
        } | null;
        if (extractorExpanders) {
            const expanderMap: {
                [extractorId: string]: ExtractorExpander<any, any>;
            } = {};
            extractorExpanders.forEach((expander) => {
                expanderMap[expander.extractorId] = expander;
            });
            extractorIdToExpander = expanderMap;
        } else {
            extractorIdToExpander = null;
        }
        forEach(iter, (item) => {
            const itemId = dataView.dataSource.getId(item);
            fieldIndexes.forEach(
                <VAL, EXP, FV>(fieldIndex: FieldIndex<ITEM, VAL, FV>) => {
                    let filterValues: Array<[FV, string]> = [];
                    let expanded = false;
                    if (extractorIdToExpander) {
                        const expander = extractorIdToExpander[
                            fieldIndex.extractor.id
                        ] as ExtractorExpander<VAL, EXP, FV> | undefined;
                        if (expander !== undefined) {
                            expanded = true;
                            const partialData = expander.expand(
                                fieldIndex.extractor.getValue(item)!
                            );
                            const transformer =
                                customOrDefaultFilterTransformer(expander);
                            if (partialData) {
                                filterValues = partialData.map((expando) => {
                                    const filterValue = transformer.getValue(
                                        expando.expandedValue
                                    );
                                    return [
                                        filterValue,
                                        transformer.asString(filterValue),
                                    ];
                                });
                            } else {
                                filterValues = [];
                            }
                        }
                    }

                    if (!expanded) {
                        const transformer = customOrDefaultFilterTransformer(
                            fieldIndex.extractor
                        );
                        const filterValue = transformer.getValue(item);
                        filterValues = [
                            [filterValue, transformer.asString(filterValue)],
                        ];
                    }
                    filterValues.forEach(([filterValue, value]) => {
                        const sanitizedValue = this.sanitize(value);
                        // No need to index empty values as we never look them up
                        if (sanitizedValue === "") {
                            return;
                        }
                        const matches =
                            fieldIndex.uniqueValueToItemIds.get(sanitizedValue);
                        if (matches !== undefined) {
                            matches.itemIds.push(itemId);
                        } else {
                            fieldIndex.uniqueValueToItemIds.set(
                                sanitizedValue,
                                {
                                    label: value,
                                    itemIds: [itemId],
                                    value: filterValue,
                                }
                            );
                            // We index a unique value (eg, "Financial control"), which
                            // will be tokenized, etc by fieldIndex.index. When
                            // matching, we will use the result (the unique value
                            // itself) to find matches in fieldIndex.uniqueValueToItemIds
                            fullTextIndex.add(
                                `${fieldIndex.extractor.id}:${sanitizedValue}`,
                                sanitizedValue
                            );
                        }
                    });
                }
            );
            i++;
        });
        this.fieldIndexes = fieldIndexes;
        this.fullTextIndex = fullTextIndex;
        const endDate = new Date();
        console.log(
            `Indexed ${i} items in `,
            endDate.getTime() - startDate.getTime(),
            "ms (",
            fieldIndexes.length,
            " indexes)"
        );
    }
    sanitize(str: string): string {
        return removeLineBreaks(str).trim().toLowerCase();
    }
    toTechnicalFilter(extractorId: string, filter: SimpleFieldFilter) {
        if (this.fieldFilterTransformers) {
            const transformer = this.fieldFilterTransformers.find(
                (tr) => tr.extractorId === extractorId
            );
            if (transformer) {
                return transformer.toTechnicalFilter(filter);
            } else {
                return filter;
            }
        } else {
            return filter;
        }
    }
    toDisplayFilter(extractorId: string, filter: SimpleFieldFilter) {
        if (this.fieldFilterTransformers) {
            const transformer = this.fieldFilterTransformers.find(
                (tr) => tr.extractorId === extractorId
            );
            if (transformer) {
                return transformer.toDisplayFilter(filter);
            } else {
                return filter;
            }
        } else {
            return filter;
        }
    }
    private getFieldIndex(extractorId: string) {
        const fieldIndex = this.fieldIndexes.find(
            (fieldIndex) => fieldIndex.extractor.id === extractorId
        );
        if (!fieldIndex) {
            throw new Error("No field index for extractor " + extractorId);
        }
        return fieldIndex;
    }
    /**
     * Given a query and a list of extractor ids to use, returns the hits,
     * ordered by relevance, for each of these extractors.
     */
    getMatches(
        rawQuery: string,
        extractorIds: Array<string>
    ): Array<MatchResult> {
        const query = this.sanitize(rawQuery);
        const queryFragments = splitQuery(this.splitter, query);
        const extractorIdsSet = new Set(extractorIds);
        let ndxHits: Array<SearchResult<string>>;
        if (queryFragments.length === 0) {
            return [];
        }
        if (queryFragments.length === 1) {
            ndxHits = this.fullTextIndex.search(query);
        } else {
            let globalFound:
                | { [docId: string]: SearchResult<string> }
                | undefined;
            queryFragments.forEach((queryFragment) => {
                const queryHits = this.fullTextIndex.search(queryFragment);
                const currentFound: { [docId: string]: SearchResult<string> } =
                    {};
                queryHits.forEach((ndxHit) => {
                    if (globalFound !== undefined) {
                        const existingResult = globalFound[ndxHit.docId];
                        if (existingResult) {
                            currentFound[ndxHit.docId] = ndxHit;
                        }
                    } else {
                        currentFound[ndxHit.docId] = ndxHit;
                    }
                });
                globalFound = currentFound;
            });
            ndxHits = globalFound ? Object.values(globalFound) : [];
            // Hits in reverse order of scores
            ndxHits.sort((hit1, hit2) => hit2.score - hit1.score);
        }
        // Make sure full matches of the search query are always at the top of the list
        const entriesForFront = [];
        for (let i = 0; i < ndxHits.length; i++) {
            const split = ndxHits[i].docId.split(":");
            if (
                split[1]
                    .toLocaleLowerCase()
                    .includes(rawQuery.toLocaleLowerCase())
            ) {
                entriesForFront.push(ndxHits[i]);
                ndxHits.splice(i, 1);
                i--; // We've removed an entry, so the index needs to decrement
            }
        }
        if (entriesForFront.length > 0) {
            ndxHits = [...entriesForFront, ...ndxHits];
        }
        const extractorIdToFieldIndex: {
            [extractorId: string]: FieldIndex<ITEM, any, unknown>;
        } = {};
        this.fieldIndexes.forEach(
            (fieldIndex) =>
                (extractorIdToFieldIndex[fieldIndex.extractor.id] = fieldIndex)
        );
        const extractorIdToOrResults: { [extractorId: string]: OrResult } = {};
        ndxHits.forEach((ndxHit) => {
            const { docId } = ndxHit;
            const [_text, extractorId, uniqueValue] = /^([^:]+):(.+)$/.exec(
                docId
            )!;
            if (!extractorIdsSet.has(extractorId)) {
                return;
            }
            const fieldIndex = extractorIdToFieldIndex[extractorId];
            const labelAndItemIds = getOrThrow(
                fieldIndex.uniqueValueToItemIds,
                uniqueValue
            );

            let orResult = extractorIdToOrResults[extractorId];
            if (!orResult) {
                orResult = {
                    extractorId,
                    matchGroups: [
                        {
                            itemIds: labelAndItemIds.itemIds,
                            value: labelAndItemIds.value,
                        },
                    ],
                };
                extractorIdToOrResults[extractorId] = orResult;
            } else {
                orResult.matchGroups.push({
                    value: labelAndItemIds.value,
                    itemIds: labelAndItemIds.itemIds,
                });
            }
        });
        const results: Array<MatchResult> = [];
        this.fieldIndexes.forEach((fieldIndex) => {
            const extractorId = fieldIndex.extractor.id;
            const orResult = extractorIdToOrResults[extractorId];
            const wildcardMatchGroup: MatchGroup = {
                value: query,
                itemIds: [],
            };
            for (const [
                uniqueValue,
                labelAndItemIds,
            ] of fieldIndex.uniqueValueToItemIds) {
                if (uniqueValue.indexOf(query) >= 0) {
                    labelAndItemIds.itemIds.forEach((itemId) => {
                        wildcardMatchGroup.itemIds.push(itemId);
                    });
                }
            }
            if (orResult) {
                results.push({
                    ...orResult,
                    wildcardMatchGroup,
                });
            } else {
                results.push({
                    extractorId,
                    matchGroups: [],
                    wildcardMatchGroup,
                });
            }
        });
        return results;
    }
    /**
     * Filters the underlying data view, and returns the filtered data view wrapped in a {QueryResult}.
     */
    find(topLevelFilter: SimpleAndFilter | null): QueryResult<ITEM> {
        if (topLevelFilter === null || topLevelFilter.filters.length === 0) {
            return {
                dataView: this.dataView,
                orResults: [],
                excludedItemIds: null,
            };
        }
        const idsToExclude = this.findIdsToExclude(topLevelFilter);
        const orFiltersWithInclusionFilters = topLevelFilter.filters.filter(
            (filter) => !!(filter.filters && filter.filters.length > 0)
        );
        if (orFiltersWithInclusionFilters.length === 0) {
            let dataView: DataView<ITEM>;
            if (idsToExclude === null || isEmpty(idsToExclude)) {
                dataView = this.dataView;
            } else {
                const itemIds: Set<string> = new Set();
                const iter = this.dataView.dataSource.iter();
                forEach(iter, (item) => {
                    const itemId = this.dataView.dataSource.getId(item);
                    itemIds.add(itemId);
                });
                const dataSource = this.dataView.dataSource.filteredByIds(
                    idsToExclude,
                    itemIds
                );
                dataView = {
                    dataSource,
                    extractorGroups: this.dataView.extractorGroups,
                    sorters: this.dataView.sorters,
                    defaultCompareItems: this.dataView.defaultCompareItems,
                };
            }
            return {
                dataView,
                orResults: [],
                excludedItemIds: idsToExclude,
            };
        }
        const orFilterMatchGroups: Array<Array<MatchGroup>> =
            orFiltersWithInclusionFilters.map(
                <FV>(orFilter: SimpleOrFilter<FV>) => {
                    const fieldIndex = this.getFieldIndex(
                        orFilter.extractorId
                    ) as FieldIndex<ITEM, any, FV>;
                    const transformer = customOrDefaultFilterTransformer(
                        fieldIndex.extractor
                    );
                    const currentFilterMatchGroups: Array<MatchGroup> = [];
                    if (!orFilter.filters) {
                        return currentFilterMatchGroups;
                    }
                    const onMatch = (uniqueValue: string) => {
                        const labelAndItemIds = getOrThrow(
                            fieldIndex.uniqueValueToItemIds,
                            uniqueValue
                        );
                        const { itemIds, value } = labelAndItemIds;
                        const filteredItemIds =
                            idsToExclude !== null
                                ? itemIds.filter(
                                      (itemId) => !idsToExclude.has(itemId)
                                  )
                                : itemIds;
                        if (filteredItemIds.length > 0) {
                            currentFilterMatchGroups.push({
                                value,
                                itemIds: filteredItemIds,
                            });
                        }
                    };
                    orFilter.filters.forEach((fieldFilter) => {
                        const matcher =
                            this.typeMatchers[fieldFilter.matchType];
                        if (!matcher) {
                            throw new Error(
                                `Matcher ${fieldFilter.matchType} not found`
                            );
                        }
                        const valueStr: string = fieldFilterValueAsString(
                            fieldFilter,
                            transformer.asString
                        );
                        matcher.match(valueStr, fieldIndex, onMatch, (val) =>
                            this.sanitize(val)
                        );
                    });
                    return currentFilterMatchGroups;
                }
            );
        // Compute intersection
        // The list of sets of ids found by each OR filter
        const itemIdsSets: Array<Set<string>> = [];
        orFilterMatchGroups.forEach((matchGroups) => {
            const itemIdsSet: Set<string> = new Set();
            matchGroups.forEach((matchGroup) => {
                matchGroup.itemIds.forEach((itemId) => itemIdsSet.add(itemId));
            });
            itemIdsSets.push(itemIdsSet);
        });

        const items: Array<ITEM> = [];
        const iter = this.dataView.dataSource.iter();
        const itemIds: Set<string> = new Set();
        forEach(iter, (item) => {
            const itemId = this.dataView.dataSource.getId(item);
            if (itemIdsSets.every((itemIdSet) => itemIdSet.has(itemId))) {
                items.push(item);
                itemIds.add(itemId);
            }
        });
        const orResults = orFilterMatchGroups.map(
            (matchGroups: MatchGroup[], i: number) => {
                const extractorId =
                    orFiltersWithInclusionFilters[i].extractorId;
                return {
                    extractorId,
                    matchGroups,
                };
            }
        );

        const dataSource = this.dataView.dataSource.filteredByIds(
            idsToExclude || new Set(),
            itemIds
        );
        const dataView = {
            dataSource,
            extractorGroups: this.dataView.extractorGroups,
            sorters: this.dataView.sorters,
            defaultCompareItems: this.dataView.defaultCompareItems,
        };
        const queryResult: QueryResult<ITEM> = {
            dataView,
            orResults,
            excludedItemIds: idsToExclude,
        };
        return queryResult;
    }

    private findIdsToExclude(
        topLevelFilter: SimpleAndFilter
    ): Set<string> | null {
        const orFiltersWithExclusionFilters = topLevelFilter.filters.filter(
            (filter) =>
                !!(
                    filter.exclusionFilters &&
                    filter.exclusionFilters.length > 0
                )
        );
        let idsToExclude: Set<string> | null;
        if (orFiltersWithExclusionFilters.length > 0) {
            const idMap: Set<string> = new Set();
            let matchCount = 0;
            orFiltersWithExclusionFilters.forEach(
                <FV>(orFilter: SimpleOrFilter<FV>) => {
                    const fieldIndex = this.getFieldIndex(
                        orFilter.extractorId
                    ) as FieldIndex<ITEM, any, FV>;
                    const transformer = customOrDefaultFilterTransformer(
                        fieldIndex.extractor
                    );
                    const onMatch = (uniqueValue: string) => {
                        const labelAndItemIds = getOrThrow(
                            fieldIndex.uniqueValueToItemIds,
                            uniqueValue
                        );
                        labelAndItemIds.itemIds.forEach((itemId) => {
                            idMap.add(itemId);
                            matchCount++;
                        });
                    };
                    orFilter.exclusionFilters?.forEach((fieldFilter) => {
                        const matcher =
                            this.typeMatchers[fieldFilter.matchType];
                        if (!matcher) {
                            throw new Error(
                                `Matcher ${fieldFilter.matchType} not found`
                            );
                        }
                        const valueStr: string = fieldFilterValueAsString(
                            fieldFilter,
                            transformer.asString
                        );
                        matcher.match(valueStr, fieldIndex, onMatch, (val) =>
                            this.sanitize(val)
                        );
                    });
                }
            );
            idsToExclude = matchCount === 0 ? null : idMap;
        } else {
            idsToExclude = null;
        }
        return idsToExclude;
    }
}

type RequestIdleCallbackHandle = number;

window.requestIdleCallback =
    window.requestIdleCallback ??
    ((callback, opts) => {
        const MS_PER_FRAME = 50;
        return setTimeout(() => {
            const start = performance.now();
            const timeRemaining = () =>
                Math.max(0, MS_PER_FRAME - (performance.now() - start));
            callback({
                get didTimeout() {
                    return timeRemaining() === 0;
                },
                timeRemaining,
            });
        }, opts?.timeout);
    });
window.cancelIdleCallback = window.cancelIdleCallback ?? clearTimeout;

interface Index<ITEM> {
    dataView: DataView<ITEM>;
    fullTextIndex: DocumentIndex<string, string>;
    fieldIndexes: Array<FieldIndex<ITEM, any, unknown>>;
}

interface UpdatedIds {
    /**
     * New ids to index
     */
    inserted?: string[];
    /**
     * Ids to remove from indexes, then trigger a re-indexing
     * This is equivalent to put those ids in both inserted & deleted
     */
    updated?: string[];
    /**
     * Ids to remove from indexes, does not trigger re-indexing
     */
    deleted?: string[];
}

type IndexerState =
    | { status: "COMPLETE" }
    | { status: "INDEXING"; indexed: Set<string> }
    | { status: "UPDATING"; toIndex: Set<string>; sizeHint: number };

interface Deferred<T> {
    promise: Promise<T>;
    complete: boolean;
    resolve: (value: T) => void;
    reject: (reason: any) => void;
}

type ValueOrPromise<T> = T | Promise<T>;
function asPromise<T>(valueOrPromise: ValueOrPromise<T>): Promise<T> {
    return Promise.resolve().then(() => valueOrPromise);
}

type DefaultCtrArgs<ITEM> = [
    dataView: ValueOrPromise<DataView<ITEM>>,
    indexedExtractorIds?: Array<string>,
    extractorExpanders?: Array<ExtractorExpander<any, any>>,
    fieldFilterTransformers?: Array<FieldFilterTransformer<ITEM>>,
    splitter?: Splitter,
];
type CopyCtrArgs<ITEM> = [
    other: AsyncDataViewIndex<ITEM>,
    dataView: ValueOrPromise<DataView<ITEM>>,
    changes: UpdatedIds,
    extractorExpanders?: Array<ExtractorExpander<any, any>>,
    fieldFilterTransformers?: Array<FieldFilterTransformer<ITEM>>,
];

class AsyncDataViewIndex<ITEM> {
    private readonly typeMatchers: Partial<
        Record<MatchType, TypeMatcher<ITEM>>
    >;
    private readonly indexer: AsyncDataViewIndexer<ITEM>;

    readonly splitter: Splitter;
    readonly fieldFilterTransformers?: Array<FieldFilterTransformer<ITEM>>;

    // Default constructor
    constructor(...args: DefaultCtrArgs<ITEM>);
    // Copy & update constructor
    constructor(...args: CopyCtrArgs<ITEM>);

    constructor(...args: DefaultCtrArgs<ITEM> | CopyCtrArgs<ITEM>) {
        let maybeFieldFilterTransformers:
            | Array<FieldFilterTransformer<ITEM>>
            | undefined;

        if (args[0] instanceof AsyncDataViewIndex) {
            // COPY CONSTRUCTOR
            const [
                other,
                dataView,
                changes,
                extractorExpanders,
                fieldFilterTransformers,
            ] = args as CopyCtrArgs<ITEM>;

            // Copy other fields, this is valid because access to them is protected by the promise.
            this.splitter = other.splitter;
            maybeFieldFilterTransformers = fieldFilterTransformers;

            this.indexer = new AsyncDataViewIndexer(
                other.indexer,
                dataView,
                changes,
                extractorExpanders
            );
        } else {
            // DEFAULT CONSTRUCTOR
            const [
                dataView,
                indexedExtractorIds,
                extractorExpanders,
                fieldFilterTransformers,
                splitter = defaultSplitter,
            ] = args as DefaultCtrArgs<ITEM>;

            this.splitter = splitter;
            maybeFieldFilterTransformers = fieldFilterTransformers;

            this.indexer = new AsyncDataViewIndexer(
                dataView,
                indexedExtractorIds,
                extractorExpanders,
                splitter
            );
        }

        this.typeMatchers = {
            [MATCH_TYPE_EXACT_IGNORE_CASE]: {
                match: (value, fieldIndex, onMatch, sanitize) => {
                    const query = sanitize(value);
                    if (fieldIndex.uniqueValueToItemIds.has(query)) {
                        onMatch(query);
                    }
                },
            },
            [MATCH_TYPE_WILDCARD]: {
                match: (value, fieldIndex, onMatch, sanitize) => {
                    const query = sanitize(value);
                    for (const uniqueValue of fieldIndex.uniqueValueToItemIds.keys()) {
                        if (uniqueValue.indexOf(query) >= 0) {
                            onMatch(uniqueValue);
                        }
                    }
                },
            },
        };
        if (maybeFieldFilterTransformers) {
            maybeFieldFilterTransformers.forEach((trans) => {
                const customTypeMatchers = trans.customTypeMatchers();
                if (customTypeMatchers) {
                    Object.entries(customTypeMatchers).forEach(
                        ([matchType, matcher]) => {
                            // SAFETY: matchType is a key of `customTypeMatchers` which is of type `Partial<Record<MatchType, TypeMatcher<ITEM>>>`,
                            // so matchType is guaranteed to be of type MatchType.
                            const mt = matchType as MatchType;
                            this.typeMatchers[mt] = matcher;
                        }
                    );
                }
            });
        }
        this.fieldFilterTransformers = maybeFieldFilterTransformers;
    }

    /**
     * Given a query and a list of extractor ids to use, returns the hits,
     * ordered by relevance, for each of these extractors.
     */
    getMatches(
        rawQuery: string,
        extractorIds: Array<string>
    ): Promise<Array<MatchResult>> {
        const query = AsyncDataViewIndex.sanitize(rawQuery);
        const queryFragments = splitQuery(this.splitter, query);
        const extractorIdsSet = new Set(extractorIds);
        let ndxHits: Array<SearchResult<string>>;
        if (queryFragments.length === 0) {
            // This fast path is required to allow immediate render of the dataview while indexing
            return Promise.resolve([]);
        }

        return this.indexer.promise.then(({ fieldIndexes, fullTextIndex }) => {
            if (queryFragments.length === 1) {
                ndxHits = fullTextIndex.search(query);
            } else {
                let globalFound:
                    | { [docId: string]: SearchResult<string> }
                    | undefined;
                queryFragments.forEach((queryFragment) => {
                    const queryHits = fullTextIndex.search(queryFragment);
                    const currentFound: {
                        [docId: string]: SearchResult<string>;
                    } = {};
                    queryHits.forEach((ndxHit) => {
                        if (globalFound !== undefined) {
                            const existingResult = globalFound[ndxHit.docId];
                            if (existingResult) {
                                currentFound[ndxHit.docId] = ndxHit;
                            }
                        } else {
                            currentFound[ndxHit.docId] = ndxHit;
                        }
                    });
                    globalFound = currentFound;
                });
                ndxHits = globalFound ? Object.values(globalFound) : [];
                ndxHits.sort((hit1, hit2) => hit1.score - hit2.score);
            }
            // Make sure full matches of the search query are always at the top of the list
            const entriesForFront = [];
            for (let i = 0; i < ndxHits.length; i++) {
                const split = ndxHits[i].docId.split(":");
                if (
                    split[1]
                        .toLocaleLowerCase()
                        .includes(rawQuery.toLocaleLowerCase())
                ) {
                    entriesForFront.push(ndxHits[i]);
                    ndxHits.splice(i, 1);
                    i--; // We've removed an entry, so the index needs to decrement
                }
            }
            if (entriesForFront.length > 0) {
                ndxHits = [...entriesForFront, ...ndxHits];
            }
            const extractorIdToFieldIndex: {
                [extractorId: string]: FieldIndex<ITEM, any, unknown>;
            } = {};
            fieldIndexes.forEach(
                (fieldIndex) =>
                    (extractorIdToFieldIndex[fieldIndex.extractor.id] =
                        fieldIndex)
            );
            const extractorIdToOrResults: { [extractorId: string]: OrResult } =
                {};
            ndxHits.forEach((ndxHit) => {
                const { docId } = ndxHit;
                const [_text, extractorId, uniqueValue] = /^([^:]+):(.+)$/.exec(
                    docId
                )!;
                if (!extractorIdsSet.has(extractorId)) {
                    return;
                }
                const fieldIndex = extractorIdToFieldIndex[extractorId];
                const labelAndItemIds = getOrThrow(
                    fieldIndex.uniqueValueToItemIds,
                    uniqueValue
                );

                let orResult = extractorIdToOrResults[extractorId];
                if (!orResult) {
                    orResult = {
                        extractorId,
                        matchGroups: [
                            {
                                value: labelAndItemIds.value,
                                itemIds: labelAndItemIds.itemIds,
                            },
                        ],
                    };
                    extractorIdToOrResults[extractorId] = orResult;
                } else {
                    orResult.matchGroups.push({
                        value: labelAndItemIds.value,
                        itemIds: labelAndItemIds.itemIds,
                    });
                }
            });
            const results: Array<MatchResult> = [];
            fieldIndexes.forEach((fieldIndex) => {
                const extractorId = fieldIndex.extractor.id;
                const orResult = extractorIdToOrResults[extractorId];
                const wildcardMatchGroup: MatchGroup = {
                    value: query,
                    itemIds: [],
                };
                for (const [
                    uniqueValue,
                    labelAndItemIds,
                ] of fieldIndex.uniqueValueToItemIds) {
                    if (uniqueValue.indexOf(query) >= 0) {
                        labelAndItemIds.itemIds.forEach((itemId) => {
                            wildcardMatchGroup.itemIds.push(itemId);
                        });
                    }
                }
                if (orResult) {
                    results.push({
                        ...orResult,
                        wildcardMatchGroup,
                    });
                } else {
                    results.push({
                        extractorId,
                        matchGroups: [],
                        wildcardMatchGroup,
                    });
                }
            });
            return results;
        });
    }

    find(topLevelFilter: SimpleAndFilter | null): Promise<QueryResult<ITEM>> {
        if (topLevelFilter === null || topLevelFilter.filters.length === 0) {
            // This fast past is required to allow immediate render of the dataview while indexing
            return this.indexer.dataViewPromise.then((dataView) => ({
                dataView,
                orResults: [],
                excludedItemIds: null,
            }));
        }

        return this.indexer.promise.then(({ fieldIndexes, dataView }) => {
            function getFieldIndex(extractorId: string) {
                const fieldIndex = fieldIndexes.find(
                    (fieldIndex) => fieldIndex.extractor.id === extractorId
                );
                if (!fieldIndex) {
                    throw new Error(
                        "No field index for extractor " + extractorId
                    );
                }
                return fieldIndex;
            }

            const idsToExclude = this.findIdsToExclude(
                topLevelFilter,
                getFieldIndex
            );
            const orFiltersWithInclusionFilters = topLevelFilter.filters.filter(
                (filter) => !!(filter.filters && filter.filters.length > 0)
            );
            if (orFiltersWithInclusionFilters.length === 0) {
                let filteredDataView: DataView<ITEM>;
                if (idsToExclude === null || isEmpty(idsToExclude)) {
                    filteredDataView = dataView;
                } else {
                    const itemIds: Set<string> = new Set();
                    const iter = dataView.dataSource.iter();
                    forEach(iter, (item) => {
                        const itemId = dataView.dataSource.getId(item);
                        itemIds.add(itemId);
                    });
                    const dataSource = dataView.dataSource.filteredByIds(
                        idsToExclude,
                        itemIds
                    );
                    filteredDataView = {
                        dataSource,
                        extractorGroups: dataView.extractorGroups,
                        sorters: dataView.sorters,
                        defaultCompareItems: dataView.defaultCompareItems,
                    };
                }
                return {
                    dataView: filteredDataView,
                    orResults: [],
                    excludedItemIds: idsToExclude,
                };
            }
            const orFilterMatchGroups: Array<Array<MatchGroup>> =
                orFiltersWithInclusionFilters.map(
                    <FV>(orFilter: SimpleOrFilter<FV>) => {
                        const fieldIndex = getFieldIndex(
                            orFilter.extractorId
                        ) as FieldIndex<ITEM, any, FV>;
                        const transformer = customOrDefaultFilterTransformer(
                            fieldIndex.extractor
                        );
                        const currentFilterMatchGroups: Array<MatchGroup> = [];
                        if (!orFilter.filters) {
                            return currentFilterMatchGroups;
                        }
                        const onMatch = (uniqueValue: string) => {
                            const labelAndItemIds = getOrThrow(
                                fieldIndex.uniqueValueToItemIds,
                                uniqueValue
                            );
                            const { value, itemIds } = labelAndItemIds;
                            const filteredItemIds =
                                idsToExclude !== null
                                    ? itemIds.filter(
                                          (itemId) => !idsToExclude.has(itemId)
                                      )
                                    : itemIds;
                            if (filteredItemIds.length > 0) {
                                currentFilterMatchGroups.push({
                                    value,
                                    itemIds: filteredItemIds,
                                });
                            }
                        };
                        orFilter.filters.forEach((fieldFilter) => {
                            const matcher =
                                this.typeMatchers[fieldFilter.matchType];
                            if (!matcher) {
                                throw new Error(
                                    `Matcher ${fieldFilter.matchType} not found`
                                );
                            }
                            const valueStr: string = fieldFilterValueAsString(
                                fieldFilter,
                                transformer.asString
                            );
                            matcher.match(
                                valueStr,
                                fieldIndex,
                                onMatch,
                                AsyncDataViewIndex.sanitize
                            );
                        });
                        return currentFilterMatchGroups;
                    }
                );
            // Compute intersection
            // The list of sets of ids found by each OR filter
            const itemIdsSets: Array<Set<string>> = [];
            orFilterMatchGroups.forEach((matchGroups) => {
                const itemIdsSet: Set<string> = new Set();
                matchGroups.forEach((matchGroup) => {
                    matchGroup.itemIds.forEach((itemId) =>
                        itemIdsSet.add(itemId)
                    );
                });
                itemIdsSets.push(itemIdsSet);
            });

            const itemIds: Set<string> = new Set();
            itemIdsSets.sort(Cmp.map((set) => set.size));
            const [firstItemIds, ...remainingSets] = itemIdsSets;
            if (firstItemIds !== undefined) {
                for (const itemId of firstItemIds) {
                    if (remainingSets.every((set) => set.has(itemId))) {
                        itemIds.add(itemId);
                    }
                }
            }
            const orResults = orFilterMatchGroups.map(
                (matchGroups: MatchGroup[], i: number) => {
                    const extractorId =
                        orFiltersWithInclusionFilters[i].extractorId;
                    return {
                        extractorId,
                        matchGroups,
                    };
                }
            );

            const dataSource = dataView.dataSource.filteredByIds(
                idsToExclude || new Set(),
                itemIds
            );
            const filteredDataView = {
                dataSource,
                extractorGroups: dataView.extractorGroups,
                sorters: dataView.sorters,
                defaultCompareItems: dataView.defaultCompareItems,
            };
            const queryResult: QueryResult<ITEM> = {
                dataView: filteredDataView,
                orResults,
                excludedItemIds: idsToExclude,
            };
            return queryResult;
        });
    }

    toTechnicalFilter(
        extractorId: string,
        filter: SimpleFieldFilter
    ): SimpleFieldFilter {
        if (this.fieldFilterTransformers) {
            const transformer = this.fieldFilterTransformers.find(
                (tr) => tr.extractorId === extractorId
            );
            if (transformer) {
                return transformer.toTechnicalFilter(filter);
            } else {
                return filter;
            }
        } else {
            return filter;
        }
    }

    toDisplayFilter(
        extractorId: string,
        filter: SimpleFieldFilter
    ): SimpleFieldFilter {
        if (this.fieldFilterTransformers) {
            const transformer = this.fieldFilterTransformers.find(
                (tr) => tr.extractorId === extractorId
            );
            if (transformer) {
                return transformer.toDisplayFilter(filter);
            } else {
                return filter;
            }
        } else {
            return filter;
        }
    }

    unload() {
        this.indexer.unload();
    }

    private findIdsToExclude(
        topLevelFilter: SimpleAndFilter,
        getFieldIndex: (extractorId: string) => FieldIndex<ITEM, any, unknown>
    ): Set<string> | null {
        const orFiltersWithExclusionFilters = topLevelFilter.filters.filter(
            (filter) =>
                !!(
                    filter.exclusionFilters &&
                    filter.exclusionFilters.length > 0
                )
        );
        let idsToExclude: Set<string> | null;
        if (orFiltersWithExclusionFilters.length > 0) {
            const idMap: Set<string> = new Set();
            let matchCount = 0;
            orFiltersWithExclusionFilters.forEach(
                <FV>(orFilter: SimpleOrFilter<FV>) => {
                    const fieldIndex = getFieldIndex(
                        orFilter.extractorId
                    ) as FieldIndex<ITEM, any, FV>;
                    const transformer = customOrDefaultFilterTransformer(
                        fieldIndex.extractor
                    );
                    const onMatch = (uniqueValue: string) => {
                        const labelAndItemIds = getOrThrow(
                            fieldIndex.uniqueValueToItemIds,
                            uniqueValue
                        );
                        labelAndItemIds.itemIds.forEach((itemId) => {
                            idMap.add(itemId);
                            matchCount++;
                        });
                    };
                    orFilter.exclusionFilters?.forEach((fieldFilter) => {
                        const matcher =
                            this.typeMatchers[fieldFilter.matchType];
                        if (!matcher) {
                            throw new Error(
                                `Matcher ${fieldFilter.matchType} not found`
                            );
                        }
                        const valueStr: string = fieldFilterValueAsString(
                            fieldFilter,
                            transformer.asString
                        );
                        matcher.match(
                            valueStr,
                            fieldIndex,
                            onMatch,
                            AsyncDataViewIndex.sanitize
                        );
                    });
                }
            );
            idsToExclude = matchCount === 0 ? null : idMap;
        } else {
            idsToExclude = null;
        }
        return idsToExclude;
    }

    static sanitize(str: string): string {
        return removeLineBreaks(str).trim().toLowerCase();
    }
}

function removeLineBreaks(str: string): string {
    // The reason that we check if we received a string even though the type only allows for strings
    // is that with broken extractors and expanders or broken filter data we can still get other types of values in here
    // so we want to make sure that we get a proper error in the console with the type we received and also make sure we
    // don't get a broken page just because linebreaks couldn't be removed. In the case of broken filters the page may still brake in different places
    if (isString(str)) {
        return str.replace(/[\n\r\u2028\u2029]/g, " ");
    } else {
        console.error(
            `removeLineBreaks called with value that isn't string: ${str} ${JSON.stringify(
                str
            )}`
        );
        return "";
    }
}

type IndexerDefaultCtrArgs<ITEM> = [
    dataView: ValueOrPromise<DataView<ITEM>>,
    indexedExtractorIds: Array<string> | undefined,
    extractorExpanders: Array<ExtractorExpander<any, any>> | undefined,
    splitter: Splitter,
];
type IndexerCopyCtrArgs<ITEM> = [
    other: AsyncDataViewIndexer<ITEM>,
    dataView: ValueOrPromise<DataView<ITEM>>,
    changes: UpdatedIds,
    extractorExpanders: Array<ExtractorExpander<any, any>> | undefined,
];

class AsyncDataViewIndexer<ITEM> {
    // Data attributes, can be copied from another indexer (as long as a single instance is indexing at a time)
    private readonly fullTextIndex: DocumentIndex<string, string>;
    private readonly indexedExtractorIds: Array<string> | undefined;
    private state: IndexerState = { status: "INDEXING", indexed: new Set() };

    // Mixed attributes, can't be copied from another indexer because they might capture some state
    private readonly fieldIndexes: Array<FieldIndex<ITEM, any, unknown>>;
    private readonly extractorIdToExpander: {
        [extractorId: string]: ExtractorExpander<any, any>;
    } | null;

    private iterator: DataIterator<ITEM> | undefined;
    private dataView: DataView<ITEM> | undefined;

    private currentDefer: Deferred<Index<ITEM>> | undefined;
    private firstIndexStart: number | undefined;
    private handle: RequestIdleCallbackHandle | undefined;
    private stopped = false;

    private static readonly PROCESS_MIN_COUNT = 10;
    private static readonly PROCESS_MIN_DURATION = 5;

    // Access to this promise for the outside only if you need the dataView before indexing completion
    readonly dataViewPromise: Promise<DataView<ITEM>>;

    // Default constructor
    constructor(...args: IndexerDefaultCtrArgs<ITEM>);
    // Copy & update constructor
    constructor(...args: IndexerCopyCtrArgs<ITEM>);

    constructor(
        ...args: IndexerDefaultCtrArgs<ITEM> | IndexerCopyCtrArgs<ITEM>
    ) {
        let shouldStartIndexing: boolean;
        let dataViewPromise: ValueOrPromise<DataView<ITEM>>;
        let maybeExtractorExpanders:
            | Array<ExtractorExpander<any, any>>
            | undefined;
        let otherFieldIndexes:
            | Array<FieldIndex<ITEM, any, unknown>>
            | undefined;

        if (args[0] instanceof AsyncDataViewIndexer) {
            // COPY CONSTRUCTOR
            const [other, dataView, changes, extractorExpanders] =
                args as IndexerCopyCtrArgs<ITEM>;
            other.stopIndexing();

            dataViewPromise = dataView;
            maybeExtractorExpanders = extractorExpanders;
            otherFieldIndexes = other.fieldIndexes;

            // Copy other fields, this is valid because access to them is protected by the promise.
            this.indexedExtractorIds = other.indexedExtractorIds;
            this.fullTextIndex = other.fullTextIndex;
            this.state = other.state;
            this.currentDefer = other.currentDefer;

            const toDelete = new Set([
                ...(changes.deleted ?? []),
                ...(changes.updated ?? []),
            ]);
            const toIndex = new Set([
                ...(changes.inserted ?? []),
                ...(changes.updated ?? []),
            ]);

            if (toDelete.size > 0) {
                // Iterate over the source field indexes because new fieldIndexes is not yet available
                // This is safe as long as we don't call an extractor or other methods that might have captured a state
                for (const fieldIndex of other.fieldIndexes) {
                    const uniqueValueToItemIds =
                        fieldIndex.uniqueValueToItemIds;
                    // Explicitly iterate over a copy of the object keys so we can safely delete object entries during iteration
                    // In FF a simple for-in seems to work but I don't want to bet on that...
                    for (const sanitizedValue of Array.from(
                        uniqueValueToItemIds.keys()
                    )) {
                        const labelAndItemIds = getOrThrow(
                            uniqueValueToItemIds,
                            sanitizedValue
                        );
                        labelAndItemIds.itemIds =
                            labelAndItemIds.itemIds.filter(
                                (id) => !toDelete.has(id)
                            );
                        if (labelAndItemIds.itemIds.length === 0) {
                            uniqueValueToItemIds.delete(sanitizedValue);
                            this.fullTextIndex.remove(
                                `${fieldIndex.extractor.id}:${sanitizedValue}`
                            );
                        }
                    }
                }
                switch (this.state.status) {
                    case "INDEXING":
                        for (const id of toDelete) {
                            this.state.indexed.delete(id);
                        }
                        break;
                    case "UPDATING":
                        for (const id of toDelete) {
                            this.state.toIndex.delete(id);
                        }
                        break;
                    case "COMPLETE":
                        break;
                    default:
                        assertNever(this.state);
                }
            }

            if (toIndex.size > 0) {
                switch (this.state.status) {
                    case "INDEXING":
                        for (const id of toIndex) {
                            this.state.indexed.delete(id);
                        }
                        break;
                    case "UPDATING":
                        for (const id of toIndex) {
                            this.state.toIndex.add(id);
                        }
                        this.state.sizeHint += toIndex.size;
                        break;
                    case "COMPLETE":
                        this.state = {
                            status: "UPDATING",
                            toIndex,
                            sizeHint: toIndex.size,
                        };
                        break;
                    default:
                        assertNever(this.state);
                }
            }

            shouldStartIndexing = this.state.status !== "COMPLETE";
        } else {
            // DEFAULT CONSTRUCTOR
            const [
                dataView,
                indexedExtractorIds,
                extractorExpanders,
                splitter,
            ] = args as IndexerDefaultCtrArgs<ITEM>;

            dataViewPromise = dataView;
            maybeExtractorExpanders = extractorExpanders;
            shouldStartIndexing = true;
            this.indexedExtractorIds = indexedExtractorIds;

            this.fullTextIndex = new DocumentIndex<string, string>({
                // This is technically unsafe,
                // since splitter could capture a state, but I have yet to see an use case for that.
                // Note: a workaround might be to capture a { current: splitter } instead
                // and copy + update that on copy constructor
                tokenizer: (query: string) => splitQuery(splitter, query),
                filter: (term: string) => term,
            });
            this.fullTextIndex.addField("uniqueValue", {
                getter: (uniqueValue: string) => uniqueValue,
            });
        }

        this.fieldIndexes = [];

        if (maybeExtractorExpanders) {
            const expanderMap: {
                [extractorId: string]: ExtractorExpander<any, any>;
            } = {};
            maybeExtractorExpanders.forEach((expander) => {
                expanderMap[expander.extractorId] = expander;
            });
            this.extractorIdToExpander = expanderMap;
        } else {
            this.extractorIdToExpander = null;
        }

        this.dataViewPromise = asPromise(dataViewPromise).then((dataView) => {
            const extractorsForIndexing = this.indexedExtractorIds
                ? getExtractors(dataView, this.indexedExtractorIds)
                : getAllExtractors(dataView);
            if (extractorsForIndexing.length === 0) {
                throw new Error("No extractors");
            }
            extractorsForIndexing.forEach((extractor) => {
                if (extractor.id.indexOf(":") >= 0) {
                    throw new Error(
                        "The character ':' in extractor ids is forbidden"
                    );
                }
            });

            for (const extractor of extractorsForIndexing) {
                const fieldIndex: FieldIndex<ITEM, any, unknown> = {
                    extractor,
                    // preserve the uniqueValueToItemIds from the otherFieldIndexes if available
                    uniqueValueToItemIds:
                        otherFieldIndexes?.find(
                            (fi) => fi.extractor.id === extractor.id
                        )?.uniqueValueToItemIds ?? new Map(),
                };
                this.fieldIndexes.push(fieldIndex);
            }

            return dataView;
        });

        if (shouldStartIndexing) {
            this.startIndexing();
        } else {
            this.currentDefer = {
                complete: true,
                promise: this.dataViewPromise.then((dataView) => ({
                    dataView,
                    fullTextIndex: this.fullTextIndex,
                    fieldIndexes: this.fieldIndexes,
                })),
                resolve: () => {
                    throw new Error(
                        "Cannot resolve an already resolved promise"
                    );
                },
                reject: () => {
                    throw new Error(
                        "Cannot reject an already resolved promise"
                    );
                },
            };
        }
    }

    get promise() {
        return (
            this.currentDefer?.promise ??
            Promise.reject(
                new Error("Indexing haven't started yet or have been unloaded")
            )
        );
    }

    unload = () => {
        if (this.currentDefer !== undefined && !this.currentDefer.complete) {
            this.currentDefer.reject(new Error("Indexer has been unloaded"));
        }
        this.currentDefer = undefined;
        this.stopIndexing();
    };

    private startIndexing = () => {
        if (this.currentDefer === undefined || this.currentDefer.complete) {
            const deferred: Partial<Deferred<Index<ITEM>>> = {
                complete: false,
            };
            deferred.promise = new Promise<Index<ITEM>>((resolve, reject) => {
                deferred.resolve = (value) => {
                    if (deferred.complete) {
                        throw new Error("Can't resolve a completed promise");
                    }
                    deferred.complete = true;
                    resolve(value);
                };
                deferred.reject = (reason: string) => {
                    if (deferred.complete) {
                        throw new Error("Can't reject a completed promise");
                    }
                    deferred.complete = true;
                    reject(new Error(reason));
                };
            });
            this.currentDefer = deferred as Deferred<Index<ITEM>>;
        }

        this.dataViewPromise.then((dataView) => {
            // A copy may have taken over the shared promise before the data view resolved.
            if (this.stopped) {
                return;
            }
            this.dataView = dataView;
            this.iterator = dataView.dataSource.iter();

            this.requestIndexing(AsyncDataViewIndexer.PROCESS_MIN_COUNT);
        });
    };

    private requestIndexing = (itemPerMS: number) => {
        this.handle = window.requestIdleCallback(
            (deadline) => {
                const before = this.progress();
                const start = performance.now();

                if (this.firstIndexStart === undefined) {
                    this.firstIndexStart = start;
                }

                let newItemsPerMS: number;
                let complete: boolean;

                try {
                    const n = Math.max(
                        AsyncDataViewIndexer.PROCESS_MIN_COUNT,
                        Math.max(
                            AsyncDataViewIndexer.PROCESS_MIN_DURATION,
                            deadline.timeRemaining()
                        ) * itemPerMS
                    );
                    complete = this.processItems(n);
                    newItemsPerMS =
                        (itemPerMS + n / (performance.now() - start)) / 2;
                } catch (e) {
                    console.error(e);
                    if (this.currentDefer) {
                        this.currentDefer.reject(e);
                    }
                    return;
                }

                const end = performance.now();
                if (complete) {
                    this.handle = undefined;
                    switch (this.state.status) {
                        case "INDEXING":
                            console.info(
                                `Indexed ${this.state.indexed.size} items in ${
                                    end - this.firstIndexStart
                                } ms [${end - start} ms in last frame] (${
                                    this.fieldIndexes.length
                                } indexes)`
                            );
                            break;
                        case "UPDATING":
                            console.info(
                                `Re-Indexed ${this.state.sizeHint} items in ${
                                    end - this.firstIndexStart
                                } ms [${end - start} ms in last frame] (${
                                    this.fieldIndexes.length
                                } indexes)`
                            );
                            break;
                        case "COMPLETE":
                            break; // Should never happen
                        default:
                            assertNever(this.state);
                    }
                    this.firstIndexStart = undefined;
                    this.state = { status: "COMPLETE" };
                    if (this.currentDefer) {
                        // processItem already requires dataView to be defined
                        this.currentDefer.resolve({
                            dataView: this.dataView!,
                            fieldIndexes: this.fieldIndexes,
                            fullTextIndex: this.fullTextIndex,
                        });
                    }
                } else {
                    console.info(
                        `Async indexing ${this.progress() - before} items in ${
                            end - start
                        } ms (${
                            this.fieldIndexes.length
                        } indexes / ${newItemsPerMS} item/ms)`
                    );
                    this.requestIndexing(newItemsPerMS);
                }
            },
            { timeout: 50 }
        );
    };

    private processItems(max: number): boolean {
        if (this.iterator === undefined || this.dataView === undefined) {
            throw new Error(
                "Iterator and dataview must be defined in order to process items"
            );
        }

        const shouldSkip = this.genSkip();
        let remainingSkips = max * 10;
        for (let i = 0; i < max; i++) {
            let item: ITEM;
            let itemId: string;

            while (true) {
                const nextItem = this.iterator.next();
                if (nextItem === undefined) {
                    return true;
                }
                item = nextItem;
                itemId = this.dataView.dataSource.getId(item);
                if (!shouldSkip(itemId)) {
                    break;
                }
                if (remainingSkips-- < 0) {
                    return false;
                }
            }

            this.fieldIndexes.forEach(
                <FV, VAL, EXP>(fieldIndex: FieldIndex<ITEM, VAL, FV>) => {
                    let filterValues: Array<[FV, string]> = [];
                    let expanded = false;
                    if (this.extractorIdToExpander) {
                        const expander = this.extractorIdToExpander[
                            fieldIndex.extractor.id
                        ] as ExtractorExpander<VAL, EXP, FV> | undefined;
                        if (expander !== undefined) {
                            const transformer =
                                customOrDefaultFilterTransformer(expander);
                            expanded = true;
                            const partialData = expander.expand(
                                fieldIndex.extractor.getValue(item)!
                            );
                            if (partialData) {
                                filterValues = partialData.map((expando) => {
                                    const filterValue = transformer.getValue(
                                        expando.expandedValue
                                    );
                                    return [
                                        filterValue,
                                        transformer.asString(filterValue),
                                    ];
                                });
                            } else {
                                filterValues = [];
                            }
                        }
                    }
                    if (!expanded) {
                        const transformer = customOrDefaultFilterTransformer(
                            fieldIndex.extractor
                        );
                        const filterValue = transformer.getValue(item);
                        filterValues = [
                            [filterValue, transformer.asString(filterValue)],
                        ];
                    }
                    filterValues.forEach(([filterValue, value]) => {
                        const sanitizedValue =
                            AsyncDataViewIndex.sanitize(value);
                        // No need to index empty values as we never look them up
                        if (sanitizedValue === "") {
                            return;
                        }
                        const matches =
                            fieldIndex.uniqueValueToItemIds.get(sanitizedValue);
                        if (matches !== undefined) {
                            matches.itemIds.push(itemId);
                        } else {
                            fieldIndex.uniqueValueToItemIds.set(
                                sanitizedValue,
                                {
                                    label: value,
                                    itemIds: [itemId],
                                    value: filterValue,
                                }
                            );
                            // We index a unique value (eg, "Financial control"), which
                            // will be tokenized, etc by fieldIndex.index. When
                            // matching, we will use the result (the unique value
                            // itself) to find matches in fieldIndex.uniqueValueToItemIds
                            this.fullTextIndex.add(
                                `${fieldIndex.extractor.id}:${sanitizedValue}`,
                                sanitizedValue
                            );
                        }
                    });
                }
            );

            switch (this.state.status) {
                case "INDEXING":
                    this.state.indexed.add(itemId);
                    break;
                case "UPDATING":
                    this.state.toIndex.delete(itemId);
                    if (this.state.toIndex.size === 0) {
                        return true;
                    }
                    break;
                case "COMPLETE":
                    break;
                default:
                    assertNever(this.state);
            }
        }
        return false;
    }

    private progress(): number {
        switch (this.state.status) {
            case "INDEXING":
                return this.state.indexed.size;
            case "UPDATING":
                return -this.state.toIndex.size;
            case "COMPLETE":
                return 0;
        }
    }

    private genSkip(): (id: string) => boolean {
        switch (this.state.status) {
            case "INDEXING": {
                const indexed = this.state.indexed;
                return (id) => indexed.has(id);
            }
            case "UPDATING": {
                const toIndex = this.state.toIndex;
                return (id) => !toIndex.has(id);
            }
            case "COMPLETE":
                return () => true;
        }
    }

    private stopIndexing() {
        this.stopped = true;
        if (this.handle !== undefined) {
            window.cancelIdleCallback(this.handle);
        }
    }
}

function indexDataView<ITEM>(
    dataView: DataView<ITEM>,
    indexedExtractorIds?: Array<string>,
    extractorExpanders?: Array<ExtractorExpander<any, any>>,
    fieldFilterTransformers?: Array<FieldFilterTransformer<ITEM>>
) {
    return new DataViewIndex(
        dataView,
        indexedExtractorIds,
        extractorExpanders,
        fieldFilterTransformers
    );
}

function addFieldFilter(
    extractorId: string,
    fieldFilter: SimpleFieldFilter,
    exclude: boolean,
    baseFilter: SimpleAndFilter | null
): SimpleAndFilter {
    if (baseFilter === null) {
        return {
            filters: [
                {
                    extractorId,
                    filters: exclude ? undefined : [fieldFilter],
                    exclusionFilters: exclude ? [fieldFilter] : undefined,
                },
            ],
        };
    }
    const orFilterIndex = baseFilter.filters.findIndex(
        (filter) => filter.extractorId === extractorId
    );
    let newFilter: SimpleAndFilter;
    if (orFilterIndex >= 0) {
        newFilter = set(baseFilter, {
            filters: baseFilter.filters.slice(),
        });
        const oldOrFilter = baseFilter.filters[orFilterIndex];
        const oldFiltersToFindIndex =
            (exclude ? oldOrFilter.exclusionFilters : oldOrFilter.filters) ||
            [];

        const found =
            oldFiltersToFindIndex.findIndex((filter) =>
                isEqual(filter, fieldFilter)
            ) >= 0;

        let newFilters: SimpleFieldFilter[] | undefined;
        let newExclusionFilters: SimpleFieldFilter[] | undefined;

        if (exclude || found) {
            newFilters = oldOrFilter.filters;
        } else if (oldOrFilter.filters) {
            newFilters = oldOrFilter.filters.concat(fieldFilter);
        } else {
            newFilters = [fieldFilter];
        }

        if (exclude && !found) {
            if (oldOrFilter.exclusionFilters) {
                newExclusionFilters =
                    oldOrFilter.exclusionFilters.concat(fieldFilter);
            } else {
                newExclusionFilters = [fieldFilter];
            }
        } else {
            newExclusionFilters = oldOrFilter.exclusionFilters;
        }

        newFilter.filters[orFilterIndex] = set(oldOrFilter, {
            filters: newFilters,
            exclusionFilters: newExclusionFilters,
        });
    } else {
        newFilter = {
            ...baseFilter,
            filters: baseFilter.filters.concat({
                extractorId,
                filters: exclude ? undefined : [fieldFilter],
                exclusionFilters: exclude ? [fieldFilter] : undefined,
            }),
        };
    }
    return newFilter;
}

function removeFieldFilter(
    extractorId: string,
    fieldFilter: SimpleFieldFilter,
    exclude: boolean,
    baseFilter: SimpleAndFilter
): SimpleAndFilter | null {
    const newFilter = set(baseFilter, {
        filters: baseFilter.filters.slice(),
    });
    const orFilterIndex = baseFilter.filters.findIndex(
        (filter) => filter.extractorId === extractorId
    );
    if (orFilterIndex === -1) {
        throw new Error("No such OR filter");
    }
    const oldOrFilter = baseFilter.filters[orFilterIndex];
    if (exclude) {
        if (
            oldOrFilter.exclusionFilters &&
            oldOrFilter.exclusionFilters.length === 1 &&
            (!oldOrFilter.filters || oldOrFilter.filters.length === 0)
        ) {
            const existingFieldFilter = oldOrFilter.exclusionFilters[0];
            // Removal of the last filter => remove the parent OR filter
            if (
                existingFieldFilter.value === fieldFilter.value &&
                existingFieldFilter.matchType === fieldFilter.matchType
            ) {
                newFilter.filters.splice(orFilterIndex, 1);
            } else {
                throw new Error("No such exclusion field filter");
            }
        } else {
            const newOrFilter = clone(oldOrFilter);
            newFilter.filters[orFilterIndex] = newOrFilter;
            if (oldOrFilter.exclusionFilters) {
                newOrFilter.exclusionFilters =
                    oldOrFilter.exclusionFilters.filter(
                        (oldFieldFilter) =>
                            oldFieldFilter.value !== fieldFilter.value ||
                            oldFieldFilter.matchType !== fieldFilter.matchType
                    );
                if (
                    newOrFilter.exclusionFilters.length ===
                    oldOrFilter.exclusionFilters.length
                ) {
                    throw new Error("No such exclusion field filter");
                }
            } else {
                throw new Error("No such exclusion field filter");
            }
        }
    } else if (
        oldOrFilter.filters &&
        oldOrFilter.filters.length === 1 &&
        (!oldOrFilter.exclusionFilters ||
            oldOrFilter.exclusionFilters.length === 0)
    ) {
        const existingFieldFilter = oldOrFilter.filters[0];
        // Removal of the last filter => remove the parent OR filter
        if (
            existingFieldFilter.value === fieldFilter.value &&
            existingFieldFilter.matchType === fieldFilter.matchType
        ) {
            newFilter.filters.splice(orFilterIndex, 1);
        } else {
            throw new Error("No such field filter");
        }
    } else {
        const newOrFilter = clone(oldOrFilter);
        newFilter.filters[orFilterIndex] = newOrFilter;
        if (oldOrFilter.filters) {
            newOrFilter.filters = oldOrFilter.filters.filter(
                (oldFieldFilter) =>
                    oldFieldFilter.value !== fieldFilter.value ||
                    oldFieldFilter.matchType !== fieldFilter.matchType
            );

            if (newOrFilter.filters.length === oldOrFilter.filters.length) {
                throw new Error("No such field filter");
            }
        } else {
            throw new Error("No such field filter");
        }
    }
    return newFilter.filters.length === 0 ? null : newFilter;
}

interface Splitter {
    split: (
        str: string,
        callback: (fragment: string, isSeparator: boolean) => void
    ) => void;
}

// TODO: Remove the field filter transformers and use the newer FV generic.
// FieldFilterTransformers expects filter.value to be a string.
function buildFieldFilterTransformer<ITEM, VAL extends string>(
    extractorId: string,
    matchType: MatchType,
    format: (value: VAL) => string,
    unformat: (value: string) => VAL
): FieldFilterTransformer<ITEM> {
    return {
        extractorId,
        toTechnicalFilter: (filter) => {
            if (typeof filter.value !== "string") {
                throw new Error(
                    "buildFieldFilterTransformer is not compatible with generic filter values, please rewrite the filter to use FV generic instead"
                );
            }
            if (filter.matchType === MATCH_TYPE_EXACT_IGNORE_CASE) {
                const unformatted = unformat(filter.value);
                if (unformatted) {
                    return {
                        value: unformatted,
                        matchType,
                    };
                } else {
                    return filter;
                }
            } else {
                return filter;
            }
        },
        toDisplayFilter: (filter) => {
            if (filter.matchType === matchType) {
                const formatted = format(filter.value as VAL);
                if (formatted) {
                    return {
                        value: formatted,
                        matchType: MATCH_TYPE_EXACT_IGNORE_CASE,
                    };
                } else {
                    return filter;
                }
            } else {
                return filter;
            }
        },
        customTypeMatchers: () => {
            const matcher: TypeMatcher<ITEM> = {
                match: (value, fieldIndex, onMatch, sanitize) => {
                    const query = sanitize(format(value as VAL));
                    if (fieldIndex.uniqueValueToItemIds.has(query)) {
                        onMatch(query);
                    }
                },
            };
            return {
                [matchType]: matcher,
            };
        },
    };
}

// Get a value from Map by a key or throw an exception otherwise.
function getOrThrow<K, V>(map: Map<K, V>, key: K): V {
    const item = map.get(key);
    if (item === undefined) {
        throw new Error(`Map expected to contain key "${key}"`);
    } else {
        return item;
    }
}

export type {
    FieldIndex,
    SimpleAndFilter,
    SimpleOrFilter,
    SimpleFieldFilter,
    TypeMatcher,
    FieldFilterTransformer,
    QueryResult,
    OrResult,
    MatchResult,
    Splitter,
};
export {
    MATCH_TYPE_EXACT_IGNORE_CASE,
    MATCH_TYPE_WILDCARD,
    DataViewIndex,
    AsyncDataViewIndex,
    defaultSplitter,
    indexDataView,
    addFieldFilter,
    removeFieldFilter,
    buildFieldFilterTransformer,
};
